import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { RefreshCw, Building2, Users } from 'lucide-react';
import { getApiBaseUrl } from '@/lib/apiBase';
import { crmApi } from '@/lib/crmApi';
import SupplierStatsPanel from '@/components/stats/SupplierStatsPanel';
import CustomerStatsPanel from '@/components/stats/CustomerStatsPanel';
import { StatsLoading, StatsError } from '@/components/stats/StatsShared';

const API = getApiBaseUrl();

export default function StatsPage() {
  const { token } = useAuth();
  const [section, setSection] = useState('supplier');
  const [supplierData, setSupplierData] = useState(null);
  const [customerData, setCustomerData] = useState(null);
  const [loadingSupplier, setLoadingSupplier] = useState(true);
  const [loadingCustomer, setLoadingCustomer] = useState(false);
  const [errorSupplier, setErrorSupplier] = useState(null);
  const [errorCustomer, setErrorCustomer] = useState(null);
  const [customerLoaded, setCustomerLoaded] = useState(false);

  const fetchSupplierStats = useCallback(async () => {
    setLoadingSupplier(true);
    setErrorSupplier(null);
    try {
      const res = await axios.get(`${API}/admin/stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSupplierData(res.data);
    } catch {
      setErrorSupplier('Не удалось загрузить статистику закупок');
    } finally {
      setLoadingSupplier(false);
    }
  }, [token]);

  const fetchCustomerStats = useCallback(async () => {
    setLoadingCustomer(true);
    setErrorCustomer(null);
    try {
      const data = await crmApi.getCustomerOrderAnalytics(token);
      setCustomerData(data);
      setCustomerLoaded(true);
    } catch {
      setErrorCustomer('Не удалось загрузить статистику заказов покупателей');
    } finally {
      setLoadingCustomer(false);
    }
  }, [token]);

  useEffect(() => {
    fetchSupplierStats();
  }, [fetchSupplierStats]);

  useEffect(() => {
    if (section === 'customer' && !customerLoaded && !loadingCustomer) {
      fetchCustomerStats();
    }
  }, [section, customerLoaded, loadingCustomer, fetchCustomerStats]);

  const handleRefresh = () => {
    fetchSupplierStats();
    if (section === 'customer' || customerLoaded) {
      fetchCustomerStats();
    }
  };

  const isLoading = section === 'supplier' ? loadingSupplier : loadingCustomer;
  const currentError = section === 'supplier' ? errorSupplier : errorCustomer;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Статистика</h1>
          <p className="text-sm text-gray-500 mt-1">
            Аналитика по заказам поставщикам и заказам покупателей
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={handleRefresh} disabled={isLoading}>
          <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
          Обновить
        </Button>
      </div>

      <Tabs value={section} onValueChange={setSection}>
        <TabsList className="mb-2">
          <TabsTrigger value="supplier">
            <Building2 className="w-4 h-4 mr-1.5" />
            Закупки (поставщики)
          </TabsTrigger>
          <TabsTrigger value="customer">
            <Users className="w-4 h-4 mr-1.5" />
            Продажи (покупатели)
          </TabsTrigger>
        </TabsList>

        <TabsContent value="supplier">
          {loadingSupplier && !supplierData ? (
            <StatsLoading />
          ) : errorSupplier && !supplierData ? (
            <StatsError message={errorSupplier} onRetry={fetchSupplierStats} />
          ) : (
            <SupplierStatsPanel data={supplierData} />
          )}
        </TabsContent>

        <TabsContent value="customer">
          {loadingCustomer && !customerData ? (
            <StatsLoading />
          ) : errorCustomer && !customerData ? (
            <StatsError message={errorCustomer} onRetry={fetchCustomerStats} />
          ) : (
            <CustomerStatsPanel data={customerData} />
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
